import { isPlatformBrowser } from "@angular/common";
import { inject, PLATFORM_ID } from "@angular/core";
import { CanActivateFn, Router, UrlTree } from "@angular/router";
import {
  catchError,
  defaultIfEmpty,
  map,
  Observable,
  of,
  switchMap,
  take,
} from "rxjs";
import { OmniarkModalService } from "@app/shared/services/modal.service";
import { OmniarkToastService } from "@app/shared/services/toast.service";
import { EnrichmentStateStore } from "../state/enrichment-state.store";
import { ChannelLinkService } from "./channel-link.service";
import { CHANNEL_PARAM, LinkChannel } from "./channel-link.util";

/** What one feature does with a channel: each keeps its own. */
export interface ChannelLinkScope {
  /** The channel the feature works on now, as a link writes it; "" for
   *  none (a role on all channels that picked nothing). */
  current(): string;
  /** Work on this channel. `anyChannel`: the role covers all channels, so
   *  the channel is the feature's to send; else the role is bound to it. */
  use(channel: LinkChannel, anyChannel: boolean): void;
}

export interface ChannelLinkGuardConfig {
  /** Where a cold load lands when the link cannot be opened. */
  listUrl: string;
  /** Called in the guard's injection context. */
  scope: () => ChannelLinkScope;
}

/** The Enrichment editor's channel: the bound role's, else the one picked
 *  on the list (the store both screens share). */
export function enrichmentLinkScope(): ChannelLinkScope {
  const store = inject(EnrichmentStateStore);
  const links = inject(ChannelLinkService);
  return {
    current: () => links.boundChannel()?.key ?? store.getChannel(),
    // A bound role's channel goes into the store the way the list writes it,
    // so coming back to the list finds its own value and reloads nothing.
    use: (channel, anyChannel) =>
      store.setChannel(anyChannel ? channel.key : links.roleChannelValue()),
  };
}

/**
 * Opens a page that works on one channel only when the role on screen may
 * work on the channel its link names (`?channel=`).
 *
 * The api takes the channel from the role: a bound role's own, whatever is
 * sent; the one sent, for a role on all channels. Without this a link from
 * a colleague on another channel opened on the reader's channel, and a role
 * on all channels opened it on whatever channel that browser used last —
 * another channel's data under the link's address, with nothing to say so.
 *
 *  - no `?channel=`  the page opens as before; the channel it works on is
 *                    written into the address, so a copied link is whole.
 *  - a role on all channels, or bound to that channel: opens.
 *  - another role of the user covers it: asks, then switches.
 *  - no role covers it, or no such channel: says so and does not open.
 *
 * Whether the product is on that channel is the page's own load to find
 * out (the api answers 404).
 */
export function channelLinkGuard(
  config: ChannelLinkGuardConfig,
): CanActivateFn {
  return (route, state) => {
    // Roles are a browser-side matter; the server render lets the page be.
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return true;
    const links = inject(ChannelLinkService);
    const router = inject(Router);
    const modal = inject(OmniarkModalService);
    const toast = inject(OmniarkToastService);
    const scope = config.scope();

    /** Not opened: stay on the page the user came from, or — a cold load
     *  has none — go to the list. */
    const refuse = (): boolean | UrlTree =>
      router.navigated ? false : router.parseUrl(config.listUrl);
    const tell = (
      title: string,
      message: string,
    ): Observable<boolean | UrlTree> =>
      modal
        .showModal({
          title,
          message,
          primaryButtonText: "OK",
          primaryButtonVariant: "primary",
          showCloseButton: false,
        })
        .pipe(
          take(1),
          defaultIfEmpty(undefined),
          map(refuse),
          catchError(() => of(refuse())),
        );

    return links.ready().pipe(
      switchMap((): Observable<boolean | UrlTree> => {
        const outcome = links.resolve(route.queryParamMap.get(CHANNEL_PARAM));
        switch (outcome.kind) {
          case "none": {
            const bound = links.boundChannel();
            if (bound) scope.use(bound, false);
            // Only a channel that would itself pass goes into the address:
            // a stale remembered one must not lock the page.
            const current = scope.current();
            if (!current || links.resolve(current).kind !== "ok") {
              return of(true);
            }
            const withChannel = router.parseUrl(state.url);
            withChannel.queryParams = {
              ...withChannel.queryParams,
              [CHANNEL_PARAM]: current,
            };
            return of(withChannel);
          }
          case "ok":
            scope.use(outcome.channel, outcome.anyChannel);
            return of(true);
          case "switch":
            return modal
              .showModal({
                title: "Switch role?",
                message: `This link is for ${outcome.channel.name}. You are on ${links.roleLabel()}. Switch to ${outcome.role.roleName} · ${outcome.anyChannel ? "All channels" : outcome.channel.name} to open it?`,
                primaryButtonText: "Switch",
                secondaryButtonText: "Cancel",
                primaryButtonVariant: "primary",
                showCloseButton: false,
                backdropDismiss: false,
              })
              .pipe(
                take(1),
                defaultIfEmpty(undefined),
                switchMap((answer) => {
                  if (answer !== "primary") return of(refuse());
                  return links.switchRole(outcome.role).pipe(
                    map((): boolean | UrlTree => {
                      scope.use(outcome.channel, outcome.anyChannel);
                      return true;
                    }),
                    catchError(() => {
                      toast.showError(
                        "Could not switch role. Please try again.",
                      );
                      return of(refuse());
                    }),
                  );
                }),
              );
          case "forbidden":
            return tell(
              "No access to this channel",
              `This link is for ${outcome.channel.name}. None of your roles covers that channel.`,
            );
          case "unknown":
            return tell(
              "Unknown channel",
              `This link names a channel that does not exist: "${outcome.value.slice(0, 60)}".`,
            );
        }
      }),
    );
  };
}
